/* ==========================================================================
   CCP Consulting — theme behaviour
   Four independent modules, no dependencies, no build step:

     1. Slider      — the reference site's signature: nearly every section is a
                      slideshow with arrows and an "n / of N" counter.
     2. Reveal      — scroll-triggered fade/slide, mirroring Dawn's
                      .scroll-trigger + .animate--* pattern.
     3. Bubble      — floating consult prompt, bottom right.
     4. Popup       — one-per-session offer of the same consult.

   Every module bails out silently when its markup is absent, so sections can be
   removed from a template without touching this file.

   Motion policy: prefers-reduced-motion disables autoplay and reveal
   transitions. Controls keep working — reduced motion means no UNREQUESTED
   movement, not a degraded page.
   ========================================================================== */
(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------- Slider */
  function initSlider(root) {
    var slides = Array.prototype.slice.call(root.querySelectorAll('[data-slide]'));
    if (slides.length === 0) return;

    var progress = root.querySelector('[data-slider-progress] span');
    var prev = root.querySelector('[data-slider-prev]');
    var next = root.querySelector('[data-slider-next]');
    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-slider-dot]'));
    var current = root.querySelector('[data-slider-current]');
    var live = root.querySelector('[data-slider-live]');

    // A single slide needs no chrome — hide it rather than render a dead 1/of 1.
    if (slides.length < 2) {
      var chrome = root.querySelector('[data-slider-controls]');
      if (chrome) chrome.hidden = true;
      slides[0].classList.add('is-active');
      return;
    }

    var i = 0;
    var timer = null;
    var delay = (parseInt(root.getAttribute('data-autoplay'), 10) || 0) * 1000;

    function paint() {
      slides.forEach(function (el, k) {
        var on = k === i;
        el.classList.toggle('is-active', on);
        // Off-screen slides must leave the tab order, or keyboard users tab
        // into content they cannot see.
        el.setAttribute('aria-hidden', on ? 'false' : 'true');
        el.querySelectorAll('a, button, input, select, textarea').forEach(function (f) {
          if (on) { f.removeAttribute('tabindex'); } else { f.setAttribute('tabindex', '-1'); }
        });
      });
      dots.forEach(function (d, k) {
        d.classList.toggle('is-active', k === i);
        d.setAttribute('aria-current', k === i ? 'true' : 'false');
      });
      if (current) current.textContent = String(i + 1);
      if (live) live.textContent = 'Slide ' + (i + 1) + ' of ' + slides.length;

      // Restart the countdown bar so the visitor can see the change coming
      // instead of being surprised by it.
      if (progress && !REDUCED && delay > 0) {
        progress.style.transition = 'none';
        progress.style.width = '0%';
        // one frame of no-transition, or the browser coalesces both writes
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            progress.style.transition = 'width ' + delay + 'ms linear';
            progress.style.width = '100%';
          });
        });
      }
    }

    function go(n) { i = (n + slides.length) % slides.length; paint(); }
    function stop() {
      if (timer) { clearInterval(timer); timer = null; }
      if (progress) {
        // freeze the bar where it is rather than letting it run on unattended
        var w = getComputedStyle(progress).width;
        progress.style.transition = 'none';
        progress.style.width = w;
      }
    }
    function play() { if (!REDUCED && delay > 0 && !timer) timer = setInterval(function () { go(i + 1); }, delay); }
    function bump(n) { go(n); stop(); play(); }

    if (prev) prev.addEventListener('click', function () { bump(i - 1); });
    if (next) next.addEventListener('click', function () { bump(i + 1); });
    dots.forEach(function (d, k) { d.addEventListener('click', function () { bump(k); }); });

    root.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') { bump(i - 1); }
      else if (e.key === 'ArrowRight') { bump(i + 1); }
      else { return; }
      e.preventDefault();
    });

    // Autoplay is a courtesy, not a fight: any hover or focus inside stops it.
    root.addEventListener('mouseenter', stop);
    root.addEventListener('mouseleave', play);
    root.addEventListener('focusin', stop);

    // Touch swipe. 40px threshold, and vertical drags are left to the page.
    var x0 = null, y0 = null;
    root.addEventListener('touchstart', function (e) {
      x0 = e.touches[0].clientX; y0 = e.touches[0].clientY;
    }, { passive: true });
    root.addEventListener('touchend', function (e) {
      if (x0 === null) return;
      var dx = e.changedTouches[0].clientX - x0;
      var dy = e.changedTouches[0].clientY - y0;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) bump(dx < 0 ? i + 1 : i - 1);
      x0 = y0 = null;
    }, { passive: true });

    paint();
    play();
  }


  /* -------------------------------------------------------------------- Nav */
  function initNav() {
    var nav = document.querySelector('[data-nav]');
    if (!nav) return;
    var toggle = nav.querySelector('.nav-toggle');
    if (!toggle) return;
    // Resolve the panel through aria-controls, the link the markup already
    // declares, rather than by guessing at the DOM shape. The button and the
    // panel are siblings, not parent and child.
    var panel = document.getElementById(toggle.getAttribute('aria-controls'));
    if (!panel) return;

    function set(open) {
      panel.classList.toggle('is-open', open);
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    }
    function isOpen() { return panel.classList.contains('is-open'); }

    toggle.addEventListener('click', function (e) {
      e.stopPropagation();
      set(!isOpen());
    });

    // Tapping a destination should close the sheet, or it stays open behind
    // the page it just navigated to on same-page anchors.
    panel.addEventListener('click', function (e) {
      if (e.target.closest('a')) set(false);
    });

    // Outside click and Escape: a menu with no way out but the button that
    // opened it is a trap on a phone.
    document.addEventListener('click', function (e) {
      if (isOpen() && !nav.contains(e.target)) set(false);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && isOpen()) { set(false); toggle.focus(); }
    });

    // Crossing the breakpoint while open would leave the desktop pill stuck in
    // its opened state.
    window.matchMedia('(min-width: 769px)').addEventListener('change', function (m) {
      if (m.matches) set(false);
    });
  }

  /* ---------------------------------------------------------------- Reveal */
  function initReveal() {
    var targets = Array.prototype.slice.call(document.querySelectorAll('[data-reveal]'));
    if (targets.length === 0) return;

    // No IntersectionObserver, or motion turned down: show everything at once.
    if (REDUCED || !('IntersectionObserver' in window)) {
      targets.forEach(function (el) { el.classList.add('is-in'); });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-in');
        io.unobserve(entry.target);   // reveal once; re-animating on scroll-back is noise
      });
    }, { rootMargin: '0px 0px -12% 0px', threshold: 0.05 });

    targets.forEach(function (el) { io.observe(el); });
  }

  /* ---------------------------------------------------------------- Bubble */
  function initBubble() {
    var bubble = document.querySelector('[data-bubble]');
    if (!bubble) return;

    var KEY = 'ccp_bubble_dismissed';
    try { if (localStorage.getItem(KEY) === '1') return; } catch (e) {}

    var close = bubble.querySelector('[data-bubble-close]');
    if (close) {
      close.addEventListener('click', function () {
        bubble.classList.remove('is-in');
        document.body.classList.remove('has-bubble');
        // Dismissal sticks across visits: re-offering something the visitor
        // already waved off is the fastest way to get the whole site ignored.
        try { localStorage.setItem(KEY, '1'); } catch (e) {}
      });
    }

    var wait = (parseInt(bubble.getAttribute('data-delay'), 10) || 4) * 1000;
    setTimeout(function () {
      bubble.classList.add('is-in');
      // Tells the page to hold room for it. On a phone the bubble is nearly
      // full width and was covering the CTA of whatever card sat at the
      // bottom of the screen.
      document.body.classList.add('has-bubble');
      var pad = function () {
        document.body.style.setProperty('--bubble-h', bubble.offsetHeight + 'px');
      };
      pad();
      if (window.ResizeObserver) new ResizeObserver(pad).observe(bubble);
      else window.addEventListener('resize', pad);
    }, wait);
  }

  /* ----------------------------------------------------------------- Popup */
  function initPopup() {
    var popup = document.querySelector('[data-popup]');
    if (!popup) return;

    var KEY = 'ccp_popup_seen';
    try { if (sessionStorage.getItem(KEY) === '1') return; } catch (e) {}

    var panel = popup.querySelector('[data-popup-panel]');
    var opener = null;

    function open() {
      opener = document.activeElement;
      popup.classList.add('is-open');
      document.documentElement.style.overflow = 'hidden';
      try { sessionStorage.setItem(KEY, '1'); } catch (e) {}
      var first = panel && panel.querySelector('a, button');
      if (first) first.focus();
    }
    function close() {
      popup.classList.remove('is-open');
      document.documentElement.style.overflow = '';
      if (opener && opener.focus) opener.focus();
    }

    popup.querySelectorAll('[data-popup-close]').forEach(function (b) {
      b.addEventListener('click', close);
    });
    popup.addEventListener('click', function (e) {
      if (e.target === popup) close();          // click the backdrop, not the panel
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && popup.classList.contains('is-open')) close();
    });

    // Fires on whichever comes first: the delay, or the visitor reading far
    // enough down to show real interest.
    var wait = (parseInt(popup.getAttribute('data-delay'), 10) || 12) * 1000;
    var depth = parseInt(popup.getAttribute('data-depth'), 10) || 45;
    var fired = false;
    function fire() { if (!fired) { fired = true; open(); } }

    setTimeout(fire, wait);
    window.addEventListener('scroll', function onScroll() {
      var h = document.documentElement.scrollHeight - window.innerHeight;
      if (h > 0 && (window.scrollY / h) * 100 >= depth) {
        window.removeEventListener('scroll', onScroll);
        fire();
      }
    }, { passive: true });
  }

  /* ------------------------------------------------------------------ Boot */
  function boot() {
    document.querySelectorAll('[data-slider]').forEach(initSlider);
    initNav();
    initReveal();
    initBubble();
    initPopup();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-run inside the theme editor, which swaps section markup without reloading.
  document.addEventListener('shopify:section:load', function (e) {
    e.target.querySelectorAll('[data-slider]').forEach(initSlider);
    initReveal();
  });
}());

/* ==========================================================================
   Article behaviour — TOC, injected CTAs, reading progress, image lightbox
   Added 2026-08-20 with the blog build.

   Everything here is progressive: the article is complete and readable with
   this file absent. Nothing below creates content, it only arranges content
   that is already in the DOM.
   ========================================================================== */
(function () {
  var post = document.querySelector('[data-post-content]');
  if (!post) return;

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---- Heading ids + table of contents ---------------------------------
     Merchant HTML from the Shopify editor has no ids, so anchors have to be
     minted here. Slugs are de-duplicated: two sections called "Eligibility"
     in one post would otherwise both answer to #eligibility and the second
     link would jump to the first heading. */
  var heads = post.querySelectorAll('h2, h3');
  var used = Object.create(null);

  function slug(text) {
    var base = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'section';
    if (!used[base]) { used[base] = 1; return base; }
    used[base] += 1;
    return base + '-' + used[base];
  }

  var items = [];
  Array.prototype.forEach.call(heads, function (h) {
    if (!h.id) h.id = slug(h.textContent.trim());
    items.push({ id: h.id, text: h.textContent.trim(), level: h.tagName === 'H3' ? 3 : 2, el: h });
  });

  var rail = document.querySelector('[data-rail]');
  var list = document.querySelector('[data-toc-list]');

  /* Under three headings a contents list is noise, not navigation. */
  if (rail && list && items.length >= 3) {
    items.forEach(function (it) {
      var li = document.createElement('li');
      if (it.level === 3) li.className = 'toc__sub';
      var a = document.createElement('a');
      a.href = '#' + it.id;
      a.textContent = it.text;
      li.appendChild(a);
      list.appendChild(li);
    });
    rail.hidden = false;

    /* Highlight the section being read. rootMargin pins the "current"
       heading to the upper third of the viewport, so the highlight changes
       when a heading reaches reading position rather than when it touches
       the very bottom edge. */
    if ('IntersectionObserver' in window) {
      var links = list.querySelectorAll('a');
      var byId = {};
      Array.prototype.forEach.call(links, function (a) { byId[a.getAttribute('href').slice(1)] = a; });

      var obs = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          Array.prototype.forEach.call(links, function (a) { a.classList.remove('is-current'); });
          var a = byId[e.target.id];
          if (a) a.classList.add('is-current');
        });
      }, { rootMargin: '-12% 0px -70% 0px', threshold: 0 });

      items.forEach(function (it) { obs.observe(it.el); });
    }
  } else if (rail && items.length < 3) {
    /* No contents worth showing, but the rail still carries CTA 2, so keep
       the rail and drop only the nav. */
    var toc = rail.querySelector('[data-toc]');
    if (toc) toc.remove();
    rail.hidden = false;
  }

  /* ---- CTA 1: after the first H2 ---------------------------------------
     Placed after the first section rather than at the top: a reader who has
     not started yet has nothing to decide about. If the post has no H2 at
     all, it goes after the first paragraph instead of being dropped. */
  var tpl = document.querySelector('template[data-inline-cta]');
  if (tpl) {
    var firstH2 = post.querySelector('h2');
    var anchor = null;

    if (firstH2) {
      /* Insert before the SECOND h2 so the CTA closes the first section
         instead of interrupting it. Falls back to the end of the first
         section's content. */
      var h2s = post.querySelectorAll('h2');
      anchor = h2s.length > 1 ? h2s[1] : null;
    }
    if (!anchor) {
      var ps = post.querySelectorAll('p');
      if (ps.length > 2) anchor = ps[2];
    }
    if (anchor && anchor.parentNode) {
      anchor.parentNode.insertBefore(tpl.content.cloneNode(true), anchor);
    }
  }

  /* ---- CTA 2 on mobile --------------------------------------------------
     On desktop the rail CTA is always visible beside the text, so it needs no
     duplicate. Below the rail breakpoint the rail is stacked above the body
     and scrolls away, so a copy is placed roughly two thirds down. */
  function placeMobileCta() {
    if (window.matchMedia('(min-width: 1000px)').matches) return;
    if (document.querySelector('[data-mobile-cta]')) return;
    var railCta = document.querySelector('.rail-cta');
    if (!railCta) return;

    var blocks = post.children;
    if (blocks.length < 6) return;
    var at = Math.floor(blocks.length * 0.62);
    var target = blocks[at];
    if (!target) return;

    var clone = railCta.cloneNode(true);
    clone.setAttribute('data-mobile-cta', '');
    clone.classList.add('rail-cta--inline');
    target.parentNode.insertBefore(clone, target);
  }
  placeMobileCta();

  /* ---- Reading progress -------------------------------------------------
     Measured against the article body, not the whole document: counting the
     footer and related-posts strip would show the bar at 70% when the reader
     has actually finished. */
  var bar = document.querySelector('[data-read-progress] span');
  if (bar && !reduce) {
    var ticking = false;
    var update = function () {
      var box = post.getBoundingClientRect();
      var total = box.height - window.innerHeight;
      var done = total > 0 ? (-box.top) / total : 0;
      done = Math.max(0, Math.min(1, done));
      bar.style.transform = 'scaleX(' + done + ')';
      ticking = false;
    };
    window.addEventListener('scroll', function () {
      if (!ticking) { ticking = true; window.requestAnimationFrame(update); }
    }, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    update();
  }

  /* ---- Wide tables ------------------------------------------------------
     Merchant tables are authored at desktop width and blow the layout out
     sideways on a phone. Wrapping each one in its own scroll container keeps
     the page itself from ever scrolling horizontally. */
  Array.prototype.forEach.call(post.querySelectorAll('table'), function (t) {
    if (t.parentNode && t.parentNode.classList.contains('table-wrap')) return;
    var wrap = document.createElement('div');
    wrap.className = 'table-wrap';
    t.parentNode.insertBefore(wrap, t);
    wrap.appendChild(t);
  });

  /* ---- Copy link --------------------------------------------------------
     Falls back to a real selection when the clipboard API is unavailable or
     refused, so the button is never a dead end. */
  var copyBtn = document.querySelector('[data-copy-link]');
  if (copyBtn) {
    copyBtn.addEventListener('click', function () {
      var url = copyBtn.getAttribute('data-url') || window.location.href;
      var done = function () {
        var old = copyBtn.textContent;
        copyBtn.textContent = 'Copied';
        copyBtn.classList.add('is-done');
        setTimeout(function () { copyBtn.textContent = old; copyBtn.classList.remove('is-done'); }, 1800);
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(done, function () { window.prompt('Copy this link', url); });
      } else {
        window.prompt('Copy this link', url);
      }
    });
  }

  /* ---- Image lightbox ---------------------------------------------------
     Article images are often screenshots of WSG paperwork, unreadable at
     column width. Click opens the full size over a scrim; Escape, a click
     outside, or the close button dismisses it. Focus is returned to the
     image that opened it. */
  var imgs = post.querySelectorAll('img');
  if (imgs.length) {
    var box = null, opener = null;

    var close = function () {
      if (!box) return;
      box.classList.remove('is-open');
      document.documentElement.style.overflow = '';
      setTimeout(function () { if (box) { box.remove(); box = null; } }, reduce ? 0 : 180);
      if (opener) { opener.focus(); opener = null; }
      document.removeEventListener('keydown', onKey);
    };

    var onKey = function (e) { if (e.key === 'Escape') close(); };

    var open = function (src, alt, from) {
      opener = from;
      box = document.createElement('div');
      box.className = 'lightbox';
      box.setAttribute('role', 'dialog');
      box.setAttribute('aria-modal', 'true');
      box.setAttribute('aria-label', alt || 'Image');

      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'lightbox__close';
      btn.setAttribute('aria-label', 'Close image');
      btn.innerHTML = '&times;';

      var big = document.createElement('img');
      big.src = src;
      big.alt = alt || '';

      box.appendChild(btn);
      box.appendChild(big);
      document.body.appendChild(box);
      document.documentElement.style.overflow = 'hidden';
      requestAnimationFrame(function () { box.classList.add('is-open'); });
      btn.focus();

      btn.addEventListener('click', close);
      box.addEventListener('click', function (e) { if (e.target === box) close(); });
      document.addEventListener('keydown', onKey);
    };

    Array.prototype.forEach.call(imgs, function (img) {
      /* Skip anything already wrapped in a link — the merchant meant that
         click to go somewhere. */
      if (img.closest('a')) return;
      img.classList.add('is-zoomable');
      img.setAttribute('tabindex', '0');
      img.setAttribute('role', 'button');
      img.setAttribute('aria-label', 'Enlarge image');
      var fire = function () { open(img.currentSrc || img.src, img.alt, img); };
      img.addEventListener('click', fire);
      img.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fire(); }
      });
    });
  }
})();
